import { getFirstSelection, getStartCol, getStartRow } from '../../../../bridge/utils/selectionUtils/tableSelectionAccessors';
import { getAllCellsBetween } from '../../../../bridge/utils/selectionUtils/tableSelectionGeometryUtils';
import { isSelectingRefColHeader } from '../../../../bridge/utils/selectionUtils/tableSelectionPredicateUtils';
import {
    addHotTableColsRight,
    addHotTableRowsBelow,
    removeHotTableCols,
    removeHotTableRows,
} from './hotTableGridOperationsUtils';
import { getColumnCount, getRowCount } from '../../../../../../../common/table/utils/tableCellDataPropertyUtils';
import { MNHotInstance } from '../../../../bridge/tableHotInstanceTypes';

/**
 * Adds a row below the current selection, or a column to the right when the ref-column header is selected.
 */
export const addHotTableRowOrColumn = (hotInstance: MNHotInstance): void => {
    const cellSelections = hotInstance?.getSelected();
    if (!cellSelections) return;

    const firstCellSelection = getFirstSelection(cellSelections);
    if (firstCellSelection && isSelectingRefColHeader(firstCellSelection)) {
        addHotTableColsRight(hotInstance, undefined);
        return;
    }

    addHotTableRowsBelow(hotInstance, undefined);
};

/**
 * Removes selected rows, or columns when the ref-column header is selected.
 */
export const removeHotTableRowOrColumn = (hotInstance: MNHotInstance): void => {
    const cellSelections = hotInstance?.getSelected();
    if (!cellSelections) return;

    const firstCellSelection = getFirstSelection(cellSelections);
    if (firstCellSelection && isSelectingRefColHeader(firstCellSelection)) {
        removeHotTableCols(hotInstance, undefined);
        return;
    }

    removeHotTableRows(hotInstance, undefined);
};

/**
 * Jumps focus out to the caption when tabbing forward from the bottom-right cell.
 */
export const jumpToCaptionFromLastCell = (hotInstance: MNHotInstance, jumpToCaption?: () => void): void => {
    const cellSelections = hotInstance?.getSelected();
    if (!cellSelections) return;

    const nRows = getRowCount(hotInstance.getData());
    const nCols = getColumnCount(hotInstance.getData());
    const firstCellSelection = getFirstSelection(cellSelections);

    if (!firstCellSelection) return;

    const isSelectingLastCell =
        getStartRow(firstCellSelection) === nRows - 1 && getStartCol(firstCellSelection) === nCols - 1;

    if (isSelectingLastCell) jumpToCaption?.();
};

/**
 * Jumps focus out to the title when reverse-tabbing from the top-left cell.
 */
export const jumpToTitleFromFirstCell = (hotInstance: MNHotInstance, jumpToTitle?: () => void): void => {
    const cellSelections = hotInstance?.getSelected();
    if (!cellSelections) return;

    const firstCellSelection = getFirstSelection(cellSelections);

    if (!firstCellSelection) return;

    const isSelectingFirstCell = getStartRow(firstCellSelection) === 0 && getStartCol(firstCellSelection) === 0;

    if (isSelectingFirstCell) jumpToTitle?.();
};

/**
 * Clears selected cells even if they were temporarily marked read-only during table interactions.
 */
export const clearSelectedCells = (hotInstance: MNHotInstance): void => {
    const cellSelections = hotInstance?.getSelected();
    if (!cellSelections) return;

    getAllCellsBetween(cellSelections).forEach(({ row, col }) => {
        hotInstance.setCellMetaObject(row, col, { readOnly: false });
    });

    hotInstance.emptySelectedCells();
};
